import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Camera, Upload, Sun, Moon, Sparkles, Droplet, AlertTriangle,
  CalendarDays, RotateCw, ShoppingBag, Crown, Heart, Check, Shield, Info, Lock,
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { postWithRetry } from "@/lib/retryablePost";
import { useAuth } from "@/context/AuthContext";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";

// SkincareCoachScreen — fully self-contained photo-first flow.
// Before scanning, a short medical-safety questionnaire personalises the
// recommendation so the AI never suggests ingredients that conflict with
// the user's conditions, allergies, prescription treatments, or pregnancy.
// Every routine result includes a clear medical disclaimer.

const SKIN_CONDITION_OPTIONS = [
  "Eczema", "Psoriasis", "Rosacea", "Severe acne", "Melasma", "Dermatitis",
];
const PRESCRIPTION_OPTIONS = [
  "Tretinoin", "Adapalene", "Isotretinoin (Accutane)", "Hydroquinone",
  "Topical antibiotic", "Other prescription",
];

function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload = () => resolve((r.result || "").toString().split(",").pop());
    r.readAsDataURL(file);
  });
}

function shopHref(product) {
  const q = encodeURIComponent(product);
  return `https://www.google.com/search?tbm=shop&q=${q}`;
}

function RoutineStep({ step, label, accent }) {
  return (
    <div className="card" style={{ padding: 14, marginBottom: 10 }} data-testid={`skin-step-${label}-${step.step}`}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div
          style={{
            width: 28, height: 28, borderRadius: "50%",
            background: accent === "am" ? "rgba(232,211,185,0.18)" : "rgba(160,180,220,0.14)",
            color: accent === "am" ? "var(--gold)" : "#a9c0e3",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', serif", fontSize: 14, fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {step.step}
        </div>
        <div style={{ flex: 1 }}>
          <div className="h3" style={{ color: "var(--text)", fontSize: 14, lineHeight: 1.3 }}>
            {step.product_type}
          </div>
          {step.why && (
            <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)", lineHeight: 1.45 }}>
              {step.why}
            </div>
          )}
          {step.how_to_apply && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 10,
                background: "var(--surface-soft)",
                borderLeft: "2px solid var(--gold-line)",
              }}
            >
              <div className="eyebrow" style={{ fontSize: 9, marginBottom: 4 }}>HOW TO APPLY</div>
              <div className="body-sm" style={{ color: "var(--text)", lineHeight: 1.5 }}>
                {step.how_to_apply}
              </div>
            </div>
          )}
          {step.product_examples?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>TRY ONE OF THESE</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {step.product_examples.map((p, i) => (
                  <a
                    key={i}
                    href={shopHref(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card"
                    style={{
                      padding: "10px 12px",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      textDecoration: "none",
                      background: "var(--bg-elev)",
                      border: "1px solid var(--line)",
                    }}
                    data-testid={`skin-product-${label}-${step.step}-${i}`}
                  >
                    <ShoppingBag size={12} strokeWidth={1.8} color="var(--gold)" />
                    <span style={{ flex: 1, color: "var(--text)", fontSize: 12.5 }}>{p}</span>
                    <span className="tiny" style={{ color: "var(--gold)" }}>Find&nbsp;it →</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SkincareCoachScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = !!user?.premium;

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // ----- Medical safety questionnaire ------------------------------------
  // Pre-filled from the user's profile.medical_safety section the first time
  // they open this screen. Editable per-scan via inline chips/toggles.
  const [safety, setSafety] = useState({
    skin_conditions: [],
    allergies: "",                    // free text, comma-separated
    sensitive_skin: false,
    prescription_treatments: [],
    pregnancy_breastfeeding: false,
    other_conditions: "",
  });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [allergyInput, setAllergyInput] = useState("");

  useEffect(() => {
    let aborted = false;
    api.get("/profile").then((r) => {
      if (aborted) return;
      const p = r.data?.profile || r.data || {};
      setSafety((s) => ({
        ...s,
        skin_conditions:         Array.isArray(p.skin_conditions) ? p.skin_conditions : s.skin_conditions,
        allergies:               Array.isArray(p.allergies) ? p.allergies.join(", ") : (p.allergies || s.allergies),
        sensitive_skin:          !!p.sensitive_skin,
        prescription_treatments: Array.isArray(p.prescription_treatments) ? p.prescription_treatments : s.prescription_treatments,
        pregnancy_breastfeeding: !!p.pregnancy_breastfeeding,
        other_conditions:        p.other_conditions || s.other_conditions,
      }));
      setAllergyInput(Array.isArray(p.allergies) ? p.allergies.join(", ") : (p.allergies || ""));
    }).catch(() => {}).finally(() => setProfileLoaded(true));
    return () => { aborted = true; };
  }, []);

  const toggleChip = (key, value) => {
    setSafety((s) => {
      const list = Array.isArray(s[key]) ? s[key] : [];
      return {
        ...s,
        [key]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value],
      };
    });
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setPreviewUrl(URL.createObjectURL(file));
    setLoading(true);
    try {
      const image_base64 = await fileToB64(file);
      const allergies = (allergyInput || "")
        .split(",").map((a) => a.trim()).filter(Boolean);
      const data = await postWithRetry(
        "/skincare/photo-routine",
        {
          image_base64,
          image_mime: file.type || "image/jpeg",
          skin_conditions: safety.skin_conditions,
          allergies,
          sensitive_skin: safety.sensitive_skin,
          prescription_treatments: safety.prescription_treatments,
          pregnancy_breastfeeding: safety.pregnancy_breastfeeding,
          other_conditions: safety.other_conditions?.trim() || null,
        },
        { timeout: 90_000, scope: "skincare-photo" },
      );
      if (data?.error === "temporary") {
        // Legacy soft-error envelope (server now uses structured 503 instead).
        setError({ type: "server_timeout", retryable: true, message: data.message || "Please try again." });
      } else {
        setResult(data);
        try { track("feature_completed", { feature: "skincare", sub_action: "analysis" }); } catch { /* ignore */ }
      }
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null); setError(null); setPreviewUrl(null);
  };

  return (
    <div className="screen with-nav fade-in" data-testid="skincare-screen">
      <div className="top-bar">
        <button
          className="icon-btn"
          onClick={() => (result || error) ? reset() : navigate(-1)}
          aria-label="Back"
          data-testid="skincare-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">SKIN COACH</div>
        {isPremium ? (
          <span className="premium-pill" data-testid="skincare-quota-pill" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Crown size={10} strokeWidth={2} /> PREMIUM
          </span>
        ) : (
          <span className="premium-pill" data-testid="skincare-quota-pill">FREE · 2/DAY</span>
        )}
      </div>

      {!result && !loading && !error && (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>PHOTO-BASED COACH</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 12, lineHeight: 1.12 }}>
            Upload a selfie.<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>I&apos;ll build your routine.</em>
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 22 }}>
            I&apos;ll spot what your skin actually needs and design your AM + PM steps — including exactly how to apply each one.
          </p>

          <input ref={cameraRef} type="file" accept="image/*" capture="user" style={{ display: "none" }}
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFile(f); }}
                 data-testid="skincare-camera-input" />
          <input ref={galleryRef} type="file" accept="image/*" style={{ display: "none" }}
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleFile(f); }}
                 data-testid="skincare-gallery-input" />

          {/* ---- Medical safety questionnaire ---- */}
          <div
            className="outfit-section"
            data-testid="skincare-safety-form"
            style={{
              marginTop: 4,
              padding: 16,
              borderRadius: 16,
              background: "rgba(160,180,220,0.05)",
              border: "1px solid rgba(160,180,220,0.18)",
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8, color: "#a9c0e3" }}>
              <Shield size={12} strokeWidth={1.8} /> QUICK SAFETY CHECK
            </div>
            <div className="tiny" style={{ color: "var(--text-faint)", marginBottom: 14, lineHeight: 1.5 }}>
              So we don&apos;t suggest anything that could conflict with your skin. {profileLoaded ? "Pre-filled from your profile — edit any time." : "Loading your profile…"}
            </div>

            {/* Skin conditions */}
            <div style={{ marginBottom: 14 }}>
              <div className="tiny" style={{ color: "var(--text-dim)", marginBottom: 6, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Any skin conditions?
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {SKIN_CONDITION_OPTIONS.map((c) => {
                  const on = safety.skin_conditions.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleChip("skin_conditions", c)}
                      className="chip"
                      data-testid={`skin-cond-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      style={{
                        background: on ? "var(--gold)" : "transparent",
                        color: on ? "#000" : "var(--text)",
                        borderColor: on ? "var(--gold)" : "var(--line)",
                        cursor: "pointer", fontWeight: on ? 700 : 500,
                      }}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Allergies */}
            <div style={{ marginBottom: 14 }}>
              <div className="tiny" style={{ color: "var(--text-dim)", marginBottom: 6, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Ingredient allergies (comma-separated)
              </div>
              <input
                type="text"
                value={allergyInput}
                onChange={(e) => setAllergyInput(e.target.value)}
                placeholder="e.g. fragrance, sulfates, retinol"
                data-testid="skincare-allergies-input"
                className="input"
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 10,
                  background: "var(--surface-soft)", color: "var(--text)",
                  border: "1px solid var(--line)", fontSize: 14,
                }}
              />
            </div>

            {/* Prescription treatments */}
            <div style={{ marginBottom: 14 }}>
              <div className="tiny" style={{ color: "var(--text-dim)", marginBottom: 6, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Currently using any prescription skincare?
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {PRESCRIPTION_OPTIONS.map((p) => {
                  const on = safety.prescription_treatments.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => toggleChip("prescription_treatments", p)}
                      className="chip"
                      data-testid={`skin-rx-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      style={{
                        background: on ? "var(--gold)" : "transparent",
                        color: on ? "#000" : "var(--text)",
                        borderColor: on ? "var(--gold)" : "var(--line)",
                        cursor: "pointer", fontWeight: on ? 700 : 500,
                      }}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Toggles row */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              <label
                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                data-testid="skin-toggle-sensitive-wrap"
              >
                <input
                  type="checkbox"
                  checked={safety.sensitive_skin}
                  onChange={(e) => setSafety((s) => ({ ...s, sensitive_skin: e.target.checked }))}
                  data-testid="skin-toggle-sensitive"
                />
                <span className="body-sm" style={{ color: "var(--text)" }}>I have sensitive skin</span>
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                data-testid="skin-toggle-pregnancy-wrap"
              >
                <input
                  type="checkbox"
                  checked={safety.pregnancy_breastfeeding}
                  onChange={(e) => setSafety((s) => ({ ...s, pregnancy_breastfeeding: e.target.checked }))}
                  data-testid="skin-toggle-pregnancy"
                />
                <span className="body-sm" style={{ color: "var(--text)" }}>Currently pregnant or breastfeeding</span>
              </label>
            </div>

            {/* Other medical conditions */}
            <div>
              <div className="tiny" style={{ color: "var(--text-dim)", marginBottom: 6, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Anything else we should know? (optional)
              </div>
              <textarea
                value={safety.other_conditions}
                onChange={(e) => setSafety((s) => ({ ...s, other_conditions: e.target.value }))}
                rows={2}
                placeholder="Other conditions or medications that may affect skincare…"
                data-testid="skincare-other-input"
                className="input"
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 10,
                  background: "var(--surface-soft)", color: "var(--text)",
                  border: "1px solid var(--line)", fontSize: 14, resize: "vertical",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
            <button type="button" className="btn btn-gold" style={{ justifyContent: "center" }}
                    onClick={() => cameraRef.current?.click()} data-testid="skincare-camera-btn">
              <Camera size={16} /> Take a selfie
            </button>
            <button type="button" className="btn btn-outline" style={{ justifyContent: "center" }}
                    onClick={() => galleryRef.current?.click()} data-testid="skincare-gallery-btn">
              <Upload size={16} /> Upload from gallery
            </button>
          </div>

          <div className="outfit-section" style={{ marginTop: 22 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>WHAT YOU&apos;LL GET</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { icon: Droplet,  title: "What I noticed",        sub: "Acne, dryness, dark spots, redness, texture, pores." },
                { icon: Heart,    title: "What's already working", sub: "Because progress should be celebrated, not graded." },
                { icon: Sun,      title: "Morning routine",       sub: "Step-by-step with how much to apply, in what order." },
                { icon: Moon,     title: "Night routine",         sub: "Targeted treatments + when results show up." },
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.title} className="card" style={{ padding: 14, display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div className="tile-icon" style={{ flexShrink: 0, width: 38, height: 38 }}>
                      <Icon size={16} strokeWidth={1.7} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>{it.title}</div>
                      <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 4, lineHeight: 1.5 }}>{it.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="tiny" style={{ marginTop: 14, color: "var(--text-faint)", textAlign: "center" }}>
            Soft daylight · no sunglasses · centered face works best.
          </div>
        </>
      )}

      {loading && (
        <div style={{ marginTop: 12 }}>
          <AnalyzerProgress
            totalSeconds={40}
            testId="skincare-analyzing"
            steps={[
              "Reading your skin…",
              "Spotting what's working…",
              "Identifying real concerns…",
              "Designing your morning routine…",
              "Designing your night routine…",
              "Picking real products you can buy…",
            ]}
          />
          {previewUrl && (
            <div style={{ marginTop: 14, borderRadius: 16, overflow: "hidden", border: "1px solid var(--line)", maxWidth: 200, marginLeft: "auto", marginRight: "auto" }}>
              <img src={previewUrl} alt="" style={{ width: "100%", display: "block" }} />
            </div>
          )}
        </div>
      )}

      {error && !loading && (
        <AnalyzerErrorCard
          error={error}
          onRetry={() => galleryRef.current?.click()}
          onPickNew={reset}
          feature="skincare-photo"
          testId="skincare-error"
        />
      )}

      {result && !loading && (
        <div data-testid="skincare-result">
          {/* Medical disclaimer — shown on EVERY routine result. The full
              copy comes from the backend so we can adjust without a frontend
              release. Stronger language when the user ticked any safety flag. */}
          {result.medical_disclaimer && (
            <div
              className="card"
              data-testid="skincare-disclaimer"
              style={{
                padding: 14,
                marginTop: 4,
                marginBottom: 12,
                border: "1px solid rgba(255,200,120,0.30)",
                background: "rgba(255,200,120,0.06)",
                display: "flex", gap: 12, alignItems: "flex-start",
              }}
            >
              <Info size={16} color="var(--gold)" strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div
                  className="tiny"
                  style={{ color: "var(--gold)", letterSpacing: "0.10em", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}
                >
                  Medical disclaimer
                </div>
                <div className="body-sm" style={{ color: "var(--text)", lineHeight: 1.55 }}>
                  {result.medical_disclaimer}
                </div>
              </div>
            </div>
          )}
          {/* Detected hero — supportive observations */}
          {result.detected && (
            <div className="glow-empty-hero" style={{ padding: 18 }} data-testid="skincare-detected-hero">
              <div className="glow-empty-badge"><Sparkles size={12} strokeWidth={2} /> WHAT I SEE</div>
              {result.detected.skin_type && (
                <div style={{ marginTop: 12 }}>
                  <div className="tiny" style={{ color: "var(--text-faint)" }}>YOUR SKIN TYPE</div>
                  <div className="h2" style={{ color: "var(--gold)", fontSize: 22, marginTop: 4, textTransform: "capitalize" }} data-testid="skincare-skin-type">
                    {result.detected.skin_type}
                  </div>
                  {result.detected.skin_type_explainer && (
                    <div className="body-sm" style={{ marginTop: 6, color: "var(--text-dim)", lineHeight: 1.5 }}>
                      {result.detected.skin_type_explainer}
                    </div>
                  )}
                </div>
              )}
              {result.summary && (
                <div className="body" style={{ marginTop: 12, color: "var(--text)", lineHeight: 1.55 }}>
                  {result.summary}
                </div>
              )}
            </div>
          )}

          {/* What's working */}
          {result.detected?.whats_working?.length > 0 && (
            <div className="outfit-section" data-testid="skincare-working">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Heart size={12} strokeWidth={1.8} /> WHAT&apos;S ALREADY WORKING
              </div>
              <div className="card" style={{ padding: 14 }}>
                <ul className="bullet-list" style={{ margin: 0 }}>
                  {result.detected.whats_working.map((w, i) => (
                    <li key={i} data-testid={`skincare-working-${i}`} style={{ color: "var(--text)" }}>{w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Concerns */}
          {result.detected?.concerns?.length > 0 && (
            <div className="outfit-section" data-testid="skincare-concerns">
              <div className="eyebrow" style={{ marginBottom: 10 }}>WHAT WE&apos;RE TARGETING</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {result.detected.concerns.map((c, i) => (
                  <div key={i} className="card" style={{ padding: 12, display: "flex", gap: 12, alignItems: "center" }} data-testid={`skincare-concern-${i}`}>
                    <div className="tile-icon" style={{ width: 32, height: 32, flexShrink: 0 }}>
                      <Droplet size={14} strokeWidth={1.7} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="h3" style={{ color: "var(--text)", fontSize: 13 }}>{c.name}</div>
                      {(c.where || c.severity) && (
                        <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 2 }}>
                          {[c.severity, c.where].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AM routine */}
          {result.am_routine?.length > 0 && (
            <div className="outfit-section" data-testid="skincare-am">
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Sun size={12} strokeWidth={1.8} /> MORNING ROUTINE
              </div>
              {result.am_routine.map((s, i) => <RoutineStep key={i} step={s} label="am" accent="am" />)}
            </div>
          )}

          {/* PM routine */}
          {result.pm_routine?.length > 0 && (
            <div className="outfit-section" data-testid="skincare-pm">
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <Moon size={12} strokeWidth={1.8} /> NIGHT ROUTINE
              </div>
              {result.pm_routine.map((s, i) => <RoutineStep key={i} step={s} label="pm" accent="pm" />)}
            </div>
          )}

          {/* Weekly extras */}
          {result.weekly_extras?.length > 0 && (
            <div className="outfit-section" data-testid="skincare-weekly">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <CalendarDays size={12} strokeWidth={1.8} /> WEEKLY EXTRAS
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {result.weekly_extras.map((w, i) => (
                  <div key={i} className="card" style={{ padding: 14 }} data-testid={`skincare-weekly-${i}`}>
                    <div className="h3" style={{ color: "var(--text)", fontSize: 13 }}>{w.product_type}</div>
                    <div className="tiny" style={{ color: "var(--gold)", marginTop: 2 }}>{w.frequency}</div>
                    {w.how_to_apply && (
                      <div className="body-sm" style={{ marginTop: 8, color: "var(--text-dim)", lineHeight: 1.5 }}>
                        {w.how_to_apply}
                      </div>
                    )}
                    {w.examples?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                        {w.examples.map((p, k) => (
                          <a key={k} href={shopHref(p)} target="_blank" rel="noopener noreferrer"
                             className="chip" style={{ textDecoration: "none", color: "var(--text)" }}>
                            {p}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What to avoid */}
          {result.what_to_avoid?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={12} strokeWidth={1.8} /> WHAT TO AVOID
              </div>
              <ul className="bullet-list">
                {result.what_to_avoid.map((w, i) => (
                  <li key={i} style={{ color: "var(--text-dim)" }} data-testid={`skincare-avoid-${i}`}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Skincare — locked product recommendations for Free users.
              Shown only when the backend surfaced a `product_recommendations_locked`
              preview (i.e. the AI suggested more products than the Free cap). */}
          {Array.isArray(result.product_recommendations_locked) && result.product_recommendations_locked.length > 0 && (
            <div className="skincare-locked-block" data-testid="skincare-locked-block">
              <div className="skincare-locked-title">
                <Lock size={13} /> Want More Options?
              </div>
              <div className="skincare-locked-desc">
                We found more products that may match your skin.
                <br /><br />
                Upgrade to IX NOVA Premium to unlock more personalized product recommendations and find the options that work best for you.
              </div>
              <div style={{ marginBottom: 14 }}>
                {result.product_recommendations_locked.map((p, i) => (
                  <div key={i} className="skincare-locked-card" data-testid={`skincare-locked-card-${i}`}>
                    <div className="skincare-locked-card-icon"><Lock size={12} /></div>
                    <div className="skincare-locked-card-name">
                      {p?.name || "Personalized product"}
                      {p?.step && <span className="tiny" style={{ display: "block", color: "var(--text-faint)", marginTop: 2 }}>{p.step}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-gold"
                onClick={() => navigate("/premium")}
                data-testid="skincare-locked-cta"
                style={{ width: "100%", justifyContent: "center" }}
              >
                <Crown size={14} /> See More With Premium
              </button>
            </div>
          )}

          {/* Results timeline */}
          {result.results_timeline && (
            <div className="outfit-section" data-testid="skincare-timeline">
              <div className="eyebrow" style={{ marginBottom: 10 }}>WHEN YOU&apos;LL SEE RESULTS</div>
              <div className="card" style={{ padding: 14 }}>
                <div className="body" style={{ color: "var(--text)", lineHeight: 1.55 }}>{result.results_timeline}</div>
              </div>
            </div>
          )}

          {/* First 30 days */}
          {result.first_30_days && (
            <div className="outfit-section" data-testid="skincare-first-30">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Check size={12} strokeWidth={1.8} /> YOUR FIRST 30 DAYS
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div className="body" style={{ color: "var(--text)", lineHeight: 1.55 }}>{result.first_30_days}</div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-outline"
            onClick={reset}
            data-testid="skincare-new-btn"
            style={{ marginTop: 14, width: "100%", justifyContent: "center" }}
          >
            <RotateCw size={14} /> Analyze a new selfie
          </button>
        </div>
      )}
    </div>
  );
}
